import { useState, useEffect, Fragment } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { convertMeetingDateTimeToGMT } from '../utils/timezoneUtils'
import { isNativePlatform } from '../utils/platform'
import {
  presentNativePaymentSheet,
  isApplePayAvailable,
  isGooglePayAvailable,
  presentApplePay,
  presentGooglePay
} from '../services/stripeService'

// Import web Stripe component (the existing one)
import StripePaymentModal from './StripePayment'

// Extend Window interface for custom properties
declare global {
  interface Window {
    lastSuccessfulPaymentId?: string;
  }
}

interface PaymentModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  amount: number // Amount in dollars
  professionalId: string
  professionalName: string
  sessionDetails: {
    date: string
    time: string
    duration: number
  }
  userTimezone: string
}

/**
 * Native Stripe Payment Component
 * Uses @capacitor-community/stripe for iOS and Android
 */
function NativeStripePayment({
  open,
  onClose,
  onSuccess,
  amount,
  professionalId,
  professionalName,
  sessionDetails,
  userTimezone
}: PaymentModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [applePayAvailable, setApplePayAvailable] = useState(false)
  const [googlePayAvailable, setGooglePayAvailable] = useState(false)

  // Check wallet availability
  useEffect(() => {
    const checkWalletAvailability = async () => {
      const applePay = await isApplePayAvailable()
      const googlePay = await isGooglePayAvailable()
      setApplePayAvailable(applePay)
      setGooglePayAvailable(googlePay)
    }

    if (open) {
      checkWalletAvailability()
    }
  }, [open])

  const createPaymentIntent = async () => {
    try {
      // Convert date/time to GMT
      const gmtDateTime = convertMeetingDateTimeToGMT(
        sessionDetails.date,
        sessionDetails.time,
        userTimezone
      )

      const { httpsCallable } = await import('firebase/functions')
      const { functions } = await import('../lib/firebase')
      const createPaymentIntentFn = httpsCallable(functions, 'createPaymentIntent')

      const result = await createPaymentIntentFn({
        amount: amount,
        professionalId: professionalId,
        sessionDetails: {
          ...sessionDetails,
          gmtDateTime: gmtDateTime
        },
        userTimezone: userTimezone
      })

      return result.data as any
    } catch (error: any) {
      console.error('Payment intent creation failed:', error)
      throw new Error(error.message || 'Failed to initialize payment')
    }
  }

  const handleNativePayment = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Create payment intent
      const paymentData = await createPaymentIntent()

      // Present native payment sheet
      const result = await presentNativePaymentSheet(paymentData)

      if (result.success) {
        // Store payment intent ID for confirmBooking
        const paymentIntentId = paymentData.clientSecret.split('_secret_')[0]
        window.lastSuccessfulPaymentId = paymentIntentId

        onSuccess()
      } else {
        setError(result.error || 'Payment failed')
      }
    } catch (error: any) {
      setError(error.message || 'Payment failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleApplePay = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const paymentData = await createPaymentIntent()
      const result = await presentApplePay(
        paymentData,
        amount * 100, // Convert to cents
        `Session with ${professionalName}`
      )

      if (result.success) {
        const paymentIntentId = paymentData.clientSecret.split('_secret_')[0]
        window.lastSuccessfulPaymentId = paymentIntentId
        onSuccess()
      } else {
        setError(result.error || 'Apple Pay failed')
      }
    } catch (error: any) {
      setError(error.message || 'Apple Pay failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGooglePay = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const paymentData = await createPaymentIntent()
      const result = await presentGooglePay(paymentData)

      if (result.success) {
        const paymentIntentId = paymentData.clientSecret.split('_secret_')[0]
        window.lastSuccessfulPaymentId = paymentIntentId
        onSuccess()
      } else {
        setError(result.error || 'Google Pay failed')
      }
    } catch (error: any) {
      setError(error.message || 'Google Pay failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[120]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.985]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-md overflow-hidden rounded-2xl md:rounded-[20px] relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                {/* Close button */}
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="absolute top-3 right-3 md:top-[18px] md:right-[18px] w-[34px] h-[34px] rounded-full inline-flex items-center justify-center z-10 cursor-pointer transition-colors"
                  style={{ background: 'rgba(0,0,0,0.45)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)', backdropFilter: 'blur(6px)' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                {/* Header */}
                <div className="px-6 md:px-8 pt-6 pb-4" style={{ borderBottom: '1px solid hsl(0 0% 20% / 0.6)' }}>
                  <div className="text-[18px] font-medium tracking-tight">Complete payment</div>
                  <p className="text-white/50 text-[12.5px] mt-1">
                    Secure payment processed by Stripe.
                  </p>
                </div>

                <div className="p-6 md:p-8">
                  {/* Booking Summary */}
                  <div className="panel-soft p-4 mb-5">
                    <div className="summary-row">
                      <span className="summary-lbl">WITH</span>
                      <span className="font-medium">{professionalName}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">DATE</span>
                      <span>{sessionDetails.date}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">TIME</span>
                      <span>{sessionDetails.time}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">SESSION</span>
                      <span>30-min · video call</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">TOTAL</span>
                      <span className="font-semibold tracking-tight text-[15px]">${amount.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Security badge */}
                  <div className="flex items-center gap-2 panel-soft p-3 text-[12px] text-white/50 mb-5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white/80 shrink-0">
                      <rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V8a5 5 0 0 1 10 0v3"/>
                    </svg>
                    Encrypted by Stripe. We never see your card number.
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="panel-soft p-4 text-[12.5px] mb-5" style={{ background: 'rgba(255,138,115,0.04)' }}>
                      <div className="flex items-start gap-2.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                        <span className="text-[#ff8a73]">{error}</span>
                      </div>
                    </div>
                  )}

                  {/* Payment Options */}
                  <div className="flex flex-col gap-2.5">
                    {/* Apple Pay */}
                    {applePayAvailable && (
                      <button
                        onClick={handleApplePay}
                        disabled={isLoading}
                        className="btn btn-primary w-full justify-center !py-3"
                        style={{ opacity: isLoading ? 0.45 : 1 }}
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                            Processing...
                          </span>
                        ) : (
                          'Pay with Apple Pay'
                        )}
                      </button>
                    )}

                    {/* Google Pay */}
                    {googlePayAvailable && (
                      <button
                        onClick={handleGooglePay}
                        disabled={isLoading}
                        className="btn btn-outline w-full justify-center !py-3"
                        style={{ opacity: isLoading ? 0.45 : 1 }}
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                            Processing...
                          </span>
                        ) : (
                          'Pay with Google Pay'
                        )}
                      </button>
                    )}

                    {/* Divider */}
                    {(applePayAvailable || googlePayAvailable) && (
                      <div className="relative py-1">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full" style={{ borderTop: '1px solid hsl(0 0% 20%)' }} />
                        </div>
                        <div className="relative flex justify-center text-[11px]">
                          <span className="px-3 text-white/40" style={{ background: 'hsl(0 0% 5%)' }}>or pay with card</span>
                        </div>
                      </div>
                    )}

                    {/* Credit Card Payment */}
                    <button
                      onClick={handleNativePayment}
                      disabled={isLoading}
                      className={`btn ${applePayAvailable || googlePayAvailable ? 'btn-outline' : 'btn-primary'} w-full justify-center !py-3`}
                      style={{ opacity: isLoading ? 0.45 : 1 }}
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                          Processing...
                        </span>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg>
                          Pay ${amount.toFixed(2)} with card
                        </>
                      )}
                    </button>

                    {/* Cancel */}
                    <button
                      onClick={onClose}
                      disabled={isLoading}
                      className="btn btn-ghost w-full justify-center"
                      style={{ opacity: isLoading ? 0.45 : 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

/**
 * Smart Payment Component Wrapper
 * Uses native Stripe on mobile, web Stripe on browsers
 */
export default function StripePaymentWrapper(props: PaymentModalProps) {
  if (isNativePlatform()) {
    return <NativeStripePayment {...props} />
  }

  // Use existing web Stripe component
  return <StripePaymentModal {...props} />
}
